import { Icon } from "@material-ui/core";
import StatusStopped from "@material-ui/icons/Block";
import NotConnected from "@material-ui/icons/Clear";
import Manager from "@material-ui/icons/HowToReg";
import Connected from "@material-ui/icons/Link";
import ShiftLeader from "@material-ui/icons/People";
import BasicUser from "@material-ui/icons/Person";
import StatusRunning from "@material-ui/icons/Refresh";
import Settings from "@material-ui/icons/Settings";
import { Autocomplete } from "Components/Autocomplete/Autocomplete";
import { AutocompleteEntry } from "Components/Autocomplete/AutocompleteEntry";
import { KeyCode } from "Components/Autocomplete/KeyCode";
import NumericTextbox from "Components/Common/Input/TextBoxes/NumericTextbox";
import ContentView from "Components/ContentView/ContentView";
import ToggleBar from "Components/ToggleBar/ToggleBar";
import ToggleOption from "Components/ToggleBar/ToggleOption/ToggleOption";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import { AuditSuggestion } from "Models/Audit/AuditSuggestion";
import { ChecklistSuggestion } from "Models/Checklists/ChecklistSuggestion";
import { SuggestionFilter } from "Models/Filters/SuggestionFilter";
import { LinkType } from "Models/Tasks/LinkType";
import { MachineStatusType } from "Models/Tasks/MachineStatusType";
import { RoleType } from "Models/Users/RoleType";
import React from "react";
import { connect } from "react-redux";
import { Action, Dispatch } from "redux";
import ServiceProvider from "ServiceProvider/ServiceProvider";
import Types from "ServiceProvider/Types";
import { IAuditTemplateAction } from "Store/AuditTemplates/AuditTemplateAction";
import { IAuditTemplateState } from "Store/AuditTemplates/AuditTemplateState";
import { IChecklistTemplateAction } from "Store/ChecklistTemplates/ChecklistTemplateAction";
import { IChecklistTemplateState } from "Store/ChecklistTemplates/ChecklistTemplateState";
import { IApplicationState } from "Store/Store";
import i18n from "Translations/i18n";
import styles from "./TaskTemplateSettings.scss";

interface IProps {
    selectedRole: string;
    selectedLinkType: string;
    selectedMachineStatusType: string;
    selectedLinkId: number | null;
    currentPlannedTime?: number;
    auditTemplateState: IAuditTemplateState;
    checklistTemplateState: IChecklistTemplateState;
    onRoleChanged(newValue: string): void;
    onPlannedTimeChanged(newValue: number): void;
    onDeeplinkChanged(newLinkType: string, newLinkId?: number): void;
    onMachineStatusChanged(newMachineStatusType: string): void;
    getAuditTemplateSuggestionsAsync: (filter: SuggestionFilter) => Action;
    getChecklistTemplateSuggestionsAsync: (filter: SuggestionFilter) => Action;
    getOneChecklistTemplateSuggestionAsync: (checklistTemplateId: number) => Action;
    getOneAuditTemplateSuggestionAsync: (auditTemplateId: number) => Action;
}

interface IState {
    suggestions: AuditSuggestion[] | ChecklistSuggestion[];
    auditTemplateState: IAuditTemplateState;
    checklistTemplateState: IChecklistTemplateState;
    showAutocomplete: boolean;
    autocompleteEntries: AutocompleteEntry[];
    deeplinkFilter: string;
    isSearchingSuggestions: boolean;
    selectedSuggestion?: AuditSuggestion | ChecklistSuggestion;
    suggestionChanged: boolean;
}

const auditTemplateAction = ServiceProvider.get<IAuditTemplateAction>(Types.IAuditTemplateAction);
const checklistTemplateAction = ServiceProvider.get<IChecklistTemplateAction>(Types.IChecklistTemplateAction);

const mapDispatchToProps = (dispatch: Dispatch) => ({
    getChecklistTemplateSuggestionsAsync: (filter: SuggestionFilter) =>
        dispatch(checklistTemplateAction.getChecklistTemplateSuggestionsAsync(filter)),
    getAuditTemplateSuggestionsAsync: (filter: SuggestionFilter) =>
        dispatch(auditTemplateAction.getAuditTemplateSuggestionsAsync(filter)),
    getOneChecklistTemplateSuggestionAsync: (checklistTemplateId: number) =>
        dispatch(checklistTemplateAction.getOneChecklistTemplateSuggestionAsync(checklistTemplateId)),
    getOneAuditTemplateSuggestionAsync: (auditTemplateId: number) =>
        dispatch(auditTemplateAction.getOneAuditTemplateSuggestionAsync(auditTemplateId)),
});

const mapStateToProps = (state: IApplicationState) => ({
    auditTemplateState: state.auditTemplateState,
    checklistTemplateState: state.checklistTemplateState
});

export class TaskTemplateSettings extends React.Component<IProps, IState> {
    private debounceTimeoutId: number;
    public static readonly DEBOUNCE_TIMEOUT: number = 500;
    public static readonly MINIMUM_SEARCH_CHARS: number = 3;

    constructor(props: IProps) {
        super(props);

        this.state = {
            suggestions: [],
            auditTemplateState: props.auditTemplateState,
            checklistTemplateState: props.checklistTemplateState,
            showAutocomplete: false,
            autocompleteEntries: [],
            deeplinkFilter: "",
            isSearchingSuggestions: false,
            suggestionChanged: false
        };

        this.roleSelectionChanged = this.roleSelectionChanged.bind(this);
        this.deeplinkSelectionChanged = this.deeplinkSelectionChanged.bind(this);
        this.plannedTimeAmountChanged = this.plannedTimeAmountChanged.bind(this);
        this.machineStatusTypeSelectionChanged = this.machineStatusTypeSelectionChanged.bind(this);

        this.onFocusHandler = this.onFocusHandler.bind(this);
        this.onBlurHandler = this.onBlurHandler.bind(this);
        this.onAutocompleteEntrySelectedHandler = this.onAutocompleteEntrySelectedHandler.bind(this);
        this.keyDownEvent = this.keyDownEvent.bind(this);
        this.onFilterChanged = this.onFilterChanged.bind(this);
        this.setAutocompleteEntries = this.setAutocompleteEntries.bind(this);
        this.clearSelectedLink = this.clearSelectedLink.bind(this);
        this.loadSelectedDeeplink = this.loadSelectedDeeplink.bind(this);

        this.loadSelectedDeeplink();
    }

    private loadSelectedDeeplink() {
        if (this.props.selectedLinkId) {
            if (this.props.selectedLinkType === LinkType.audit) {
                this.props.getOneAuditTemplateSuggestionAsync(this.props.selectedLinkId);
            }
            else if (this.props.selectedLinkType === LinkType.checklist) {
                this.props.getOneChecklistTemplateSuggestionAsync(this.props.selectedLinkId);
            }
        }
    }

    public roleSelectionChanged(newValue: string) {
        this.props.onRoleChanged(newValue);
    }

    public deeplinkSelectionChanged(newValue: string) {
        this.props.onDeeplinkChanged(newValue, undefined);
        this.setState({ suggestionChanged: true, deeplinkFilter: "" });
    }

    public plannedTimeAmountChanged(newValue: number) {
        this.props.onPlannedTimeChanged(newValue);
    }

    public machineStatusTypeSelectionChanged(newValue: string) {
        this.props.onMachineStatusChanged(newValue);
    }

    public onFocusHandler() {
        this.setState({ showAutocomplete: this.state.deeplinkFilter.length > 0 });
    }

    public onBlurHandler() {
        this.setState({ showAutocomplete: false });
    }

    public onAutocompleteEntrySelectedHandler(entry: AutocompleteEntry) {
        this.props.onDeeplinkChanged(this.props.selectedLinkType, entry.item ? entry.item.id : undefined);
        this.setState({ selectedSuggestion: entry.item, suggestionChanged: true });
    }

    public keyDownEvent(e: React.KeyboardEvent<HTMLInputElement>) {
        const inputValue = e.currentTarget.value;
        if (e.keyCode === KeyCode.BACKSPACE_KEY_CODE && inputValue.length === 0) {
            this.clearSelectedLink();
        }
    }

    public clearSelectedLink() {
        this.props.onDeeplinkChanged(this.props.selectedLinkType, undefined);
        this.setState({ selectedSuggestion: undefined });
    }

    public onFilterChanged(e: React.FormEvent<HTMLInputElement>) {
        const inputValue = e.currentTarget.value;
        this.setState({
            deeplinkFilter: inputValue
        });
        if (this.debounceTimeoutId) {
            window.clearTimeout(this.debounceTimeoutId);
        }

        this.debounceTimeoutId = window.setTimeout(this.setAutocompleteEntries, TaskTemplateSettings.DEBOUNCE_TIMEOUT);
    }

    public setAutocompleteEntries() {
        if (this.state.deeplinkFilter.length < TaskTemplateSettings.MINIMUM_SEARCH_CHARS) {
            this.setState({ autocompleteEntries: [], showAutocomplete: this.state.deeplinkFilter.length !== 0 });
        }
        else {
            this.setState({ showAutocomplete: true, isSearchingSuggestions: true });
            const filter = new SuggestionFilter();
            filter.query = this.state.deeplinkFilter.trim();
            if (this.props.selectedLinkType === LinkType.checklist) {
                this.props.getChecklistTemplateSuggestionsAsync(filter);
            }
            else if (this.props.selectedLinkType === LinkType.audit) {
                this.props.getAuditTemplateSuggestionsAsync(filter);
            }
            else {
                this.setState({
                    autocompleteEntries: []
                });
            }
        }
    }

    public getNoItemsFoundMessage(): string {

        if (this.state.isSearchingSuggestions) {
            return i18n.t("common.searching");
        }

        if (this.state.deeplinkFilter.length < TaskTemplateSettings.MINIMUM_SEARCH_CHARS) {
            return i18n.t("tasks.settings.connect.minimumSearchLength").replace("{minimalChars}", TaskTemplateSettings.MINIMUM_SEARCH_CHARS.toString());
        }

        if (this.props.selectedLinkType === LinkType.checklist) {
            return i18n.t("tasks.settings.connect.noChecklistsFound");
        }

        if (this.props.selectedLinkType === LinkType.audit) {
            return i18n.t("tasks.settings.connect.noAuditsFound");
        }

        return "";
    }

    public static getDerivedStateFromProps(nextProps: IProps, prevState: IState): Partial<IState> {
        const partialState: Partial<IState> = {};
        let newSuggestions = false;

        if (nextProps.selectedLinkType === LinkType.audit) {
            if (prevState.suggestions !== nextProps.auditTemplateState.suggestions) {
                partialState.suggestions = nextProps.auditTemplateState.suggestions;
                newSuggestions = true;
            }

            if (prevState.selectedSuggestion !== nextProps.auditTemplateState.selectedSuggestion && !prevState.suggestionChanged) {
                partialState.selectedSuggestion = nextProps.auditTemplateState.selectedSuggestion;
            }
        }
        else if (nextProps.selectedLinkType === LinkType.checklist) {
            if (prevState.suggestions !== nextProps.checklistTemplateState.suggestions) {
                partialState.suggestions = nextProps.checklistTemplateState.suggestions;
                newSuggestions = true;
            }

            if (prevState.selectedSuggestion !== nextProps.checklistTemplateState.selectedSuggestion && !prevState.suggestionChanged) {
                partialState.selectedSuggestion = nextProps.checklistTemplateState.selectedSuggestion;
            }
        }

        if (newSuggestions) {
            partialState.autocompleteEntries = partialState.suggestions!.map(item => {
                const autocompleteEntry: AutocompleteEntry = { key: item.id, text: item.name, item };
                return autocompleteEntry;
            });
            partialState.isSearchingSuggestions = false;
        }

        return partialState;
    }

    public render() {
        return (
            <ContentView backgroundIsGray={true}>
                <h1><Settings />{i18n.t("tasks.settings.title")}</h1>
                <div className={styles.settings}>
                    <div className={styles.table}>
                        <div className={styles.row}>
                            <div className={styles.column}>
                                <span>{i18n.t("tasks.settings.role.label")}</span>
                            </div>
                            <div className={styles.column}>
                                <ToggleBar selectedItem={this.props.selectedRole} onChange={this.roleSelectionChanged} whiteBackground={true} fullWidth={true}>
                                    <ToggleOption id="tgoRoleBasicUser" value={RoleType.basic}><BasicUser /> {i18n.t("authentication.roles.basic")}</ToggleOption>
                                    <ToggleOption id="tgoRoleShiftLeader" value={RoleType.shiftleader}><ShiftLeader /> {i18n.t("authentication.roles.shift_leader")}</ToggleOption>
                                    <ToggleOption id="tgoRoleManager" value={RoleType.manager}><Manager /> {i18n.t("authentication.roles.manager")}</ToggleOption>
                                </ToggleBar>
                            </div>
                        </div>
                        <div className={styles.row}>
                            <div className={styles.column}>
                                <span>{i18n.t("tasks.settings.connect.label")}</span>
                            </div>
                            <div className={styles.column}>
                                <ToggleBar selectedItem={this.props.selectedLinkType} onChange={this.deeplinkSelectionChanged} whiteBackground={true} fullWidth={true}>
                                    <ToggleOption id="tgoLinkNone" value={LinkType.none}><NotConnected /> {i18n.t("tasks.settings.connect.none")}</ToggleOption>
                                    <ToggleOption id="tgoLinkAudit" value={LinkType.audit}><Connected /> {i18n.t("tasks.settings.connect.audit")}</ToggleOption>
                                    <ToggleOption id="tgoLinkChecklist" value={LinkType.checklist}><Connected /> {i18n.t("tasks.settings.connect.checklist")}</ToggleOption>
                                </ToggleBar>
                            </div>
                        </div>
                        {((this.props.selectedLinkType === LinkType.checklist || this.props.selectedLinkType === LinkType.audit)
                            && (<div className={styles.row}>
                                <div className={styles.column} />
                                <div className={`${styles.column} ${styles.autosuggestColumn}`}>

                                    <Autocomplete
                                        show={this.state.showAutocomplete}
                                        entries={this.state.autocompleteEntries}
                                        onBlur={this.onBlurHandler}
                                        onFocus={this.onFocusHandler}
                                        onEntrySelected={this.onAutocompleteEntrySelectedHandler}
                                        noItemsFoundMessage={this.getNoItemsFoundMessage()}
                                        isSearching={this.state.isSearchingSuggestions}>

                                        <div className={`${styles.searchWrapper} ${styles.searchbar}`}>
                                            <span className={styles.icon}><MagnifyIcon /></span>
                                            {
                                                this.props.selectedLinkId && this.state.selectedSuggestion &&
                                                <div className={styles.chip}>
                                                    {this.state.selectedSuggestion.name}
                                                    <Icon className={styles.closeIcon} onClick={this.clearSelectedLink}>close</Icon>
                                                </div>
                                            }
                                            <input id="txtLinkSearch" className={styles.input} value={this.state.deeplinkFilter} onKeyDown={this.keyDownEvent} onChange={this.onFilterChanged} placeholder={i18n.t("tasks.settings.connect.placeholder")} />
                                        </div>
                                    </Autocomplete>
                                </div>
                            </div>
                            ))
                        }
                        <div className={styles.row}>
                            <div className={styles.column}>
                                <span>{i18n.t("tasks.settings.machineStatus.label")}</span>
                            </div>
                            <div className={styles.column}>
                                <ToggleBar selectedItem={this.props.selectedMachineStatusType} onChange={this.machineStatusTypeSelectionChanged} whiteBackground={true} fullWidth={true}>
                                    <ToggleOption id="tgoMachineStatusNotApplicable" value={MachineStatusType.not_applicable}><NotConnected /> {i18n.t("tasks.settings.machineStatus.not_applicable")}</ToggleOption>
                                    <ToggleOption id="tgoMachineStatusNotStop" value={MachineStatusType.stopped}><StatusStopped /> {i18n.t("tasks.settings.machineStatus.stopped")}</ToggleOption>
                                    <ToggleOption id="tgoMachineStatusNotRunning" value={MachineStatusType.running}><StatusRunning /> {i18n.t("tasks.settings.machineStatus.running")}</ToggleOption>
                                </ToggleBar>
                            </div>
                        </div>
                        <div className={styles.row}>
                            <div className={styles.column}>
                                <span>{i18n.t("tasks.settings.plannedTime.label")}</span>
                            </div>
                            <div className={styles.column}>
                                <NumericTextbox className={styles.numericTextBox} onChange={this.plannedTimeAmountChanged} value={this.props.currentPlannedTime} min={0} max={999} allowEmpty={true} />
                                <span>{i18n.t("tasks.settings.plannedTime.minutes")}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </ContentView>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(TaskTemplateSettings);


// WEBPACK FOOTER //
// ./src/Components/Pages/TaskTemplateDetailView/TaskTemplateSettings/TaskTemplateSettings.tsx